import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * Tipo de densidade para o componente Tab, definindo o espaçamento e tamanho dos elementos.
 */
type TabDensity = 'small' | 'medium' | 'large'

/**
 * Tipo de modo de cor para o componente Tab, definindo o esquema de cores utilizado.
 */
type TabColorMode = 'dark'

/**
 * Tipo de alinhamento dos itens da tab na barra de navegação.
 */
type TabAlignItems = 'start' | 'center' | 'end'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/tab?tab=designer).
 *
 * @slot default - Slot para inserir os itens das abas (br-tab-item)
 *
 * @part container - Parte para o contêiner principal das abas
 * @part nav - Parte para a navegação das abas
 */
@Component({
  tag: 'br-tab',
  styleUrl: 'tab.scss',
  shadow: true,
})
export class Tab {
  /**
   * Referência ao elemento host do componente.
   */
  @Element() el!: HTMLBrTabElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * A propriedade 'density' define a densidade do componente.
   */
  @Prop({ reflect: true }) readonly density: TabDensity = 'medium'

  /**
   * Define se o Tab usará um esquema de cores escuro.
   */
  @Prop({ reflect: true }) readonly colorMode?: TabColorMode

  /**
   * Label de acessibilidade para a navegação das tabs
   */
  @Prop({ reflect: true }) readonly label: string = 'Abas de Navegação'

  /**
   * Define o alinhamento horizontal dos itens da tab na barra de navegação.
   */
  @Prop({ reflect: true }) readonly alignItemsTab: TabAlignItems = 'start'

  /**
   * Desabilita o scroll automático da área de conteúdo.
   * Por padrão, o scroll vertical fica apenas na região de conteúdo.
   */
  @Prop({ reflect: true }) readonly scrollDisabled: boolean = false

  /**
   * Define a altura máxima da área de conteúdo, ativando o scroll interno quando o conteúdo ultrapassa essa altura.
   */
  @Prop({ reflect: true }) readonly height?: string

  /**
   * Lista dos tab items registrados
   */
  @State() private tabItems: HTMLBrTabItemElement[] = []

  /**
   * ID do tab atualmente ativo
   */
  @State() private activeTabId: string = ''

  /**
   * Flag para controlar se o activeTabId já foi inicializado
   */
  private activeTabInitialized = false

  /**
   * Evento disparado quando um tab é ativado.
   */
  @Event() brTabChange!: EventEmitter<{ tabId: string; tabIndex: number }>

  /**
   * Referência ao elemento de slot para monitorar mudanças nos tab items filhos.
   */
  private slotElement: HTMLSlotElement | undefined

  /**
   * Observador de mutação para detectar mudanças nos tab items filhos
   */
  private mutationObserver: MutationObserver | undefined

  /**
   * Observa mudanças no tab ativo para atualizar os estados
   */
  @Watch('activeTabId')
  activeTabChanged(newId: string) {
    this.updateTabStates()
    const activeIndex = this.tabItems.findIndex((item) => item.tabItemId === newId)
    this.brTabChange.emit({ tabId: newId, tabIndex: activeIndex })
  }

  /**
   * Observa mudanças no color mode para propagar aos filhos
   */
  @Watch('colorMode')
  colorModeChanged(newValue: string) {
    this.tabItems.forEach((item) => {
      if (newValue !== undefined && newValue !== null && typeof newValue === 'string') {
        item.setAttribute('color-mode', newValue)
      } else {
        item.removeAttribute('color-mode')
      }
    })
  }

  /**
   * Escuta evento de mudança de propriedades dos tab-items filhos.
   * Este listener garante que o componente pai seja re-renderizado quando
   * propriedades como tabItemTitle, icon, counter ou onlyIcon mudam dinamicamente.
   */
  @Listen('brTabItemPropsChange')
  handleTabItemPropsChange() {
    // Força re-renderização para atualizar a navegação
    this.tabItems = [...this.tabItems]
  }

  /**
   * Registra os tab items filhos e garante que cada um tenha um ID único.
   * Também propaga o color mode para os tab items.
   * @returns void
   */
  private registerTabItems = () => {
    if (this.slotElement === undefined || this.slotElement === null) return

    const assignedElements = this.slotElement.assignedElements() as HTMLBrTabItemElement[]
    this.tabItems = assignedElements.filter((el) => el.tagName?.toLowerCase() === 'br-tab-item')

    // Garante que cada tab item tenha um ID único
    this.tabItems.forEach((item) => {
      if (
        item.tabItemId === undefined ||
        item.tabItemId === null ||
        (typeof item.tabItemId === 'string' && item.tabItemId === '') ||
        item.hasAttribute('tab-item-id') === false
      ) {
        const id = Helpers.generateUniqueId()
        item.tabItemId = id
        item.setAttribute('tab-item-id', id)
      }

      // Propaga color mode
      if (this.colorMode !== undefined && this.colorMode !== null && typeof this.colorMode === 'string') {
        item.setAttribute('color-mode', this.colorMode)
      }
    })

    // Só define activeTabId se ainda não foi inicializado
    if (!this.activeTabInitialized && this.tabItems.length > 0) {
      const activeItem = this.tabItems.find((item) => item.isActive === true && !item.disabled)
      const firstEnabled = this.tabItems.find((item) => !item.disabled)
      this.activeTabId = activeItem?.tabItemId ?? firstEnabled?.tabItemId ?? this.tabItems[0].tabItemId
      this.activeTabInitialized = true
    }

    this.updateTabStates()
  }

  /**
   * Atualiza o estado ativo de todos os tab items
   * Comunicação direta similar ao Vue
   */
  private updateTabStates() {
    this.tabItems.forEach((item) => {
      const isActive = item.tabItemId === this.activeTabId
      item.isActive = isActive

      // Atualiza atributos para compatibilidade
      if (isActive) {
        item.setAttribute('is-active', 'true')
        item.setAttribute('aria-selected', 'true')
      } else {
        item.removeAttribute('is-active')
        item.removeAttribute('aria-selected')
      }
    })
  }

  /**
   * Manipula o clique em um botão de tab
   */
  private handleTabClick(tabId: string) {
    const item = this.tabItems.find((i) => i.tabItemId === tabId)
    if (item?.disabled) return
    if (this.activeTabId !== tabId) {
      this.activeTabId = tabId
    }
  }

  /**
   * Manipula a navegação por teclado nos botões de tab para melhorar a acessibilidade
   */
  private handleTabButtonClick = (event: MouseEvent) => {
    const tabId = (event.currentTarget as HTMLElement | null)?.getAttribute('data-tab-id')
    if (typeof tabId === 'string' && tabId.trim() !== '') {
      this.handleTabClick(tabId)
    }
  }

  /**
   * Manipula o evento de pressionamento de tecla nos botões de tab para melhorar a acessibilidade
   * @param event Evento de teclado
   */
  private handleTabButtonKeyDown = (event: KeyboardEvent) => {
    const indexAttr = (event.currentTarget as HTMLElement | null)?.getAttribute('data-tab-index')
    const index = indexAttr !== null ? Number(indexAttr) : NaN
    if (!Number.isNaN(index)) {
      this.handleKeyNavigation(index, event)
    }
  }

  /**
   * Encontra o índice habilitado mais próximo na direção indicada (1 = frente, -1 = trás).
   * Retorna currentIndex se todos os outros itens estiverem desabilitados.
   */
  private findEnabledIndex(currentIndex: number, direction: 1 | -1): number {
    const len = this.tabItems.length
    let next = (currentIndex + direction + len) % len
    let iterations = 0
    while (this.tabItems[next]?.disabled && iterations < len) {
      next = (next + direction + len) % len
      iterations++
    }
    return this.tabItems[next]?.disabled ? currentIndex : next
  }

  /**
   * Manipula navegação por teclado (acessibilidade)
   */
  private handleKeyNavigation(currentIndex: number, event: KeyboardEvent) {
    let newIndex = currentIndex

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        newIndex = this.findEnabledIndex(currentIndex, 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        newIndex = this.findEnabledIndex(currentIndex, -1)
        break
      case 'Home': {
        event.preventDefault()
        const firstEnabled = this.tabItems.findIndex((item) => !item.disabled)
        newIndex = firstEnabled !== -1 ? firstEnabled : 0
        break
      }
      case 'End': {
        event.preventDefault()
        const lastEnabled = [...this.tabItems]
          .map((item, i) => ({ item, i }))
          .reverse()
          .find(({ item }) => !item.disabled)
        newIndex = lastEnabled !== undefined ? lastEnabled.i : this.tabItems.length - 1
        break
      }
      case 'ArrowDown':
        event.preventDefault()
        // Foca no conteúdo do painel ativo
        const activePanel = this.tabItems[currentIndex]
        if (activePanel !== undefined && activePanel !== null && typeof activePanel === 'object') {
          const focusableElements = activePanel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
          if (focusableElements.length > 0) {
            ;(focusableElements[0] as HTMLElement).focus()
          }
        }
        return
      default:
        return
    }

    // Ativa e foca o novo tab
    if (newIndex !== currentIndex) {
      this.activeTabId = this.tabItems[newIndex].tabItemId
      // O foco será definido no próximo render através do tabindex
      setTimeout(() => {
        const buttons = this.el.shadowRoot?.querySelectorAll('.tab-item button')
        ;(buttons?.[newIndex] as HTMLElement)?.focus()
      }, 0)
    }
  }

  /**
   * Retorna o mapeamento de classes CSS do componente
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-tab': true,
      small: this.density === 'small',
      medium: this.density === 'medium',
      large: this.density === 'large',
      'dark-mode': this.colorMode === 'dark',
      'scroll-disabled': this.scrollDisabled,
    }
  }

  /**
   * Retorna o mapeamento de classes CSS para cada item de navegação
   */
  private getTabItemCssClassMap(isActive: boolean, isDisabled: boolean): CssClassMap {
    return {
      'tab-item': true,
      active: isActive,
      disabled: isDisabled,
      'dark-mode': this.colorMode === 'dark',
    }
  }

  /**
   * Retorna o alinhamento em formato flexbox
   */
  private getAlignItemsStyle(): string {
    const alignMap = {
      start: 'flex-start',
      center: 'center',
      end: 'flex-end',
    }
    return alignMap[this.alignItemsTab] || 'flex-start'
  }

  /**
   * Verifica se o valor fornecido é uma altura válida para o componente, aceitando unidades CSS modernas e a palavra-chave 'auto'.
   * @param value Valor a ser verificado
   * @returns true se o valor for uma altura válida, caso contrário false
   */
  private isValidHeight(value?: string): boolean {
    if (!value || typeof value !== 'string') return false

    const trimmed = value.trim()

    // aceita auto
    if (trimmed === 'auto') return true

    // aceita funções CSS modernas
    if (/^(calc|min|max|clamp)\(.+\)$/.test(trimmed)) return true

    // aceita unidades CSS modernas
    const cssUnitRegex = /^\d+(\.\d+)?(px|%|vh|vw|dvh|svh|lvh|vmin|vmax|rem|em)$/

    if (cssUnitRegex.test(trimmed)) return true

    return false
  }

  /**
   * Retorna a altura resolvida do componente, considerando valores válidos e emitindo um aviso para valores inválidos.
   * @returns A altura resolvida ou undefined se o valor for inválido
   */
  private getResolvedHeight(): string | undefined {
    if (!this.height) return undefined

    if (this.isValidHeight(this.height)) {
      return this.height
    }

    console.warn(`[br-tab]: valor inválido para "height": "${this.height}". Valor ignorado.`)

    return undefined
  }

  /**
   * Ciclo de vida. Executado antes do componente ser carregado no DOM.
   * Garante que os tab items sejam registrados mesmo que o evento slotchange não seja disparado.
   */
  componentWillLoad() {
    // Garante que registerTabItems seja chamado mesmo se slotchange não disparar
    this.registerTabItems()
  }

  /**
   * Ciclo de vida. Executado quando o componente é removido do DOM.
   * Desconecta o MutationObserver para evitar vazamentos de memória.
   */
  disconnectedCallback() {
    if (
      this.mutationObserver !== undefined &&
      this.mutationObserver !== null &&
      typeof this.mutationObserver === 'object'
    ) {
      this.mutationObserver.disconnect()
    }
  }

  /**
   * Renderiza o componente.
   * @returns JSX.Element
   */
  render() {
    const hasCounter = this.tabItems.some((item) => item.counter)
    const resolvedHeight = this.getResolvedHeight()

    return (
      <Host>
        <div
          class={this.getCssClassMap()}
          id={this.customId}
          part="container"
          style={resolvedHeight !== undefined ? { '--br-tab-height': resolvedHeight } : undefined}
        >
          {/* Navegação das tabs */}
          <nav class="tab-nav" data-counter={hasCounter ? 'true' : 'false'} aria-label={this.label} part="nav">
            <ul role="tablist" style={{ justifyContent: this.getAlignItemsStyle() }}>
              {this.tabItems.map((item, index) => {
                const isActive = item.tabItemId === this.activeTabId
                const showOnlyIcon = item.onlyIcon || item.hasAttribute('only-icon')

                return (
                  <li
                    key={item.tabItemId}
                    class={this.getTabItemCssClassMap(isActive, item.disabled)}
                    role="presentation"
                  >
                    <button
                      type="button"
                      id={`tab-button-${item.tabItemId}`}
                      role="tab"
                      aria-selected={isActive ? 'true' : 'false'}
                      aria-disabled={item.disabled ? 'true' : undefined}
                      aria-controls={`tab-panel-${item.tabItemId}`}
                      tabindex={isActive && !item.disabled ? 0 : -1}
                      disabled={item.disabled || undefined}
                      data-tab-id={item.tabItemId}
                      data-tab-index={index}
                      onClick={this.handleTabButtonClick}
                      onKeyDown={this.handleTabButtonKeyDown}
                    >
                      <span class="name">
                        {item.icon && (
                          <span class="icon">
                            <br-icon icon-name={item.icon}></br-icon>
                          </span>
                        )}
                        {!showOnlyIcon && <span>{item.tabItemTitle}</span>}
                      </span>
                    </button>
                    {item.counter && <span class="results">({item.counter})</span>}
                  </li>
                )
              })}
            </ul>
          </nav>

          <div class="tab-content">
            <slot
              ref={(el) => {
                this.slotElement = el as HTMLSlotElement
              }}
              onSlotchange={this.registerTabItems}
            ></slot>
          </div>
        </div>
      </Host>
    )
  }
}
